// src/services/dynamicQuestionInstanceBuilder.js
// Phase 5: Instance Builder - Orchestrates the full dynamic question pipeline

import {
  Interval,
  IntervalSet,
  PredicateEvaluator,
  GraphFeatureExtractor,
  SeededRandom,
  roundTo,
  getNestedValue
} from './dynamicQuestionService.js';

import {
  ALL_TEMPLATES,
  getTemplatesForFunction,
  StatementCategory,
  StatementType
} from './statementTemplates.js';


// ═══════════════════════════════════════════════════════════════════════════════
// PARAMETER GENERATOR
// ═══════════════════════════════════════════════════════════════════════════════

export class ParameterGenerator {
  constructor(seed = null) {
    this.rng = new SeededRandom(seed || Date.now());
  }

  /**
   * Generate parameters from configuration
   * @param {object} config - Parameter configuration
   * @returns {object} Generated parameters
   */
  generate(config) {
    const params = {};
    
    for (const [key, spec] of Object.entries(config)) {
      if (spec.values) {
        // Discrete set of values
        let available = [...spec.values];
        if (spec.exclude) {
          available = available.filter(v => !spec.exclude.includes(v));
        }
        params[key] = this.rng.choice(available);
      } else if (spec.min !== undefined && spec.max !== undefined) {
        // Range with step
        const step = spec.step || 1;
        const values = [];
        for (let v = spec.min; v <= spec.max; v += step) {
          if (!spec.exclude || !spec.exclude.includes(v)) {
            values.push(roundTo(v, 4));
          }
        }
        params[key] = this.rng.choice(values);
      }
    }
    
    return params;
  }

  /**
   * Validate parameters meet constraints
   */
  validate(params, constraints) {
    const issues = [];
    
    for (const constraint of constraints || []) {
      const evaluator = new PredicateEvaluator({ params });
      if (!evaluator.evaluate(constraint.predicate)) {
        issues.push(constraint.message || 'Constraint violated');
      }
    }
    
    return { valid: issues.length === 0, issues };
  }

  /**
   * Generate valid parameters (with retry)
   */
  generateValid(config, constraints, maxAttempts = 50) {
    for (let i = 0; i < maxAttempts; i++) {
      const params = this.generate(config);
      const { valid, issues } = this.validate(params, constraints);
      if (valid) return { params, attempts: i + 1 };
    }
    
    // Fall back to first generated params
    return { params: this.generate(config), attempts: maxAttempts, warning: 'Could not satisfy all constraints' };
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// STATEMENT SELECTOR
// ═══════════════════════════════════════════════════════════════════════════════

export class StatementSelector {
  constructor(templates = ALL_TEMPLATES) {
    this.templates = templates;
  }

  /**
   * Select and evaluate statements for a given context
   */
  selectStatements(functionType, context, config) {
    const {
      includeCategories = Object.values(StatementCategory),
      excludeCategories = [],
      totalOptions = { min: 4, max: 6 },
      correctOptions = { min: 2, max: 4 },
      incorrectOptions = { min: 1, max: 3 },
      requiredCategories = [],
      targetDifficulty = { min: 1, max: 4 },
      shuffleOptions = true
    } = config;

    // Get applicable templates
    let templates = this.templates.filter(t => 
      t.functionTypes.includes(functionType) &&
      includeCategories.includes(t.category) &&
      !excludeCategories.includes(t.category) &&
      t.difficulty >= targetDifficulty.min &&
      t.difficulty <= targetDifficulty.max
    );

    // Evaluate all predicates
    const evaluator = new PredicateEvaluator(context);
    const evaluated = templates.map(t => ({
      ...t,
      isCorrect: evaluator.evaluate(t.predicate),
      evaluatedText: this.interpolateText(t.text, context)
    }));

    // Separate correct and incorrect
    const correctStatements = evaluated.filter(s => s.isCorrect);
    const incorrectStatements = evaluated.filter(s => !s.isCorrect);

    // Check satisfiability
    if (correctStatements.length < correctOptions.min) {
      return { 
        success: false, 
        reason: `Not enough correct statements (${correctStatements.length} < ${correctOptions.min})` 
      };
    }
    if (incorrectStatements.length < incorrectOptions.min) {
      return { 
        success: false, 
        reason: `Not enough incorrect statements (${incorrectStatements.length} < ${incorrectOptions.min})` 
      };
    }

    // Select options
    const selectedCorrect = this.selectFromPool(
      correctStatements, 
      correctOptions.min, 
      correctOptions.max,
      requiredCategories,
      context.rng
    );

    const selectedIncorrect = this.selectFromPool(
      incorrectStatements,
      incorrectOptions.min,
      Math.min(incorrectOptions.max, totalOptions.max - selectedCorrect.length),
      [],
      context.rng
    );

    let selected = [...selectedCorrect, ...selectedIncorrect];

    // Ensure total count is within bounds
    if (selected.length > totalOptions.max) {
      selected = selected.slice(0, totalOptions.max);
    }

    // Shuffle if requested
    if (shuffleOptions && context.rng) {
      selected = context.rng.shuffle(selected);
    }

    // Format as options
    const options = selected.map((s, index) => ({
      id: `opt_${s.id}_${index}`,
      templateId: s.id,
      text: s.evaluatedText,
      isCorrect: s.isCorrect,
      category: s.category,
      targetSkill: s.targetSkill,
      misconception: s.misconception || null,
      difficulty: s.difficulty
    }));

    return {
      success: true,
      options,
      stats: {
        correctCount: options.filter(o => o.isCorrect).length,
        incorrectCount: options.filter(o => !o.isCorrect).length,
        categories: [...new Set(options.map(o => o.category))]
      }
    };
  }

  /**
   * Select items from pool with category requirements
   */
  selectFromPool(pool, min, max, requiredCategories, rng) {
    const selected = [];
    const remaining = [...pool];

    // First, ensure required categories are covered
    for (const category of requiredCategories) {
      const fromCategory = remaining.filter(s => s.category === category);
      if (fromCategory.length > 0) {
        const choice = rng ? rng.choice(fromCategory) : fromCategory[0];
        selected.push(choice);
        remaining.splice(remaining.indexOf(choice), 1);
      }
    }

    // Fill up to target count
    const targetCount = min + (rng ? rng.nextInt(0, max - min) : 0);
    
    while (selected.length < targetCount && remaining.length > 0) {
      const choice = rng ? rng.choice(remaining) : remaining[0];
      selected.push(choice);
      remaining.splice(remaining.indexOf(choice), 1);
    }

    return selected;
  }

  /**
   * Interpolate placeholder text with actual values
   */
  interpolateText(text, context) {
    return text.replace(/\{([^}]+)\}/g, (match, path) => {
      // Handle array access syntax like features.verticalAsymptotes[0]
      if (path.includes('[')) {
        const arrayMatch = path.match(/^(.+)\[(\d+)\]$/);
        if (arrayMatch) {
          const [, basePath, indexStr] = arrayMatch;
          const arr = getNestedValue(context, basePath);
          const index = parseInt(indexStr);
          if (Array.isArray(arr) && arr[index] !== undefined) {
            const v = arr[index];
            return typeof v === 'number' ? roundTo(v, 2).toString() : String(v);
          }
        }
        return match;
      }
      
      const value = getNestedValue(context, path);
      if (value === undefined || value === null) return match;
      
      // Format numbers nicely
      if (typeof value === 'number') {
        if (Number.isInteger(value)) return String(value);
        return roundTo(value, 2).toString();
      }
      
      return String(value);
    });
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// GRAPH CONFIG BUILDER
// ═══════════════════════════════════════════════════════════════════════════════

export class GraphConfigBuilder {
  /**
   * Build Plotly-compatible graph configuration
   */
  static build(functionType, params, features, displayConfig = {}) {
    const {
      showFeatures = ['intercepts', 'asymptotes'],
      domain = [-10, 10],
      gridLines = true,
      pedagogicalMode = true,
      colors = {
        primary: '#6366f1',
        secondary: '#22c55e',
        asymptote: '#ef4444',
        feature: '#f59e0b'
      }
    } = displayConfig;

    const traces = [];
    const annotations = [];
    const shapes = [];

    // Main function trace
    const mainTrace = this.generateFunctionTrace(functionType, params, domain);
    mainTrace.line = { color: colors.primary, width: 3 };
    mainTrace.name = features.equation;
    traces.push(mainTrace);

    // Feature markers
    if (showFeatures.includes('intercepts')) {
      // X-intercepts
      features.roots?.forEach((root, i) => {
        if (root >= domain[0] && root <= domain[1]) {
          traces.push({
            x: [root],
            y: [0],
            mode: 'markers',
            marker: { color: colors.feature, size: 10, symbol: 'circle' },
            name: `x-intercept ${i + 1}`,
            showlegend: false
          });
        }
      });

      // Y-intercept
      if (features.yIntercept !== null && 0 >= domain[0] && 0 <= domain[1]) {
        traces.push({
          x: [0],
          y: [features.yIntercept],
          mode: 'markers',
          marker: { color: colors.feature, size: 10, symbol: 'circle' },
          name: 'y-intercept',
          showlegend: false
        });
      }
    }

    // Vertex (for quadratic)
    if (showFeatures.includes('vertex') && features.vertex) {
      traces.push({
        x: [features.vertex.x],
        y: [features.vertex.y],
        mode: 'markers',
        marker: { color: colors.secondary, size: 12, symbol: 'diamond' },
        name: 'Vertex',
        showlegend: false
      });
    }

    // Asymptotes
    if (showFeatures.includes('asymptotes')) {
      // Vertical asymptotes
      features.verticalAsymptotes?.forEach(x => {
        shapes.push({
          type: 'line',
          x0: x, x1: x,
          y0: domain[0] * 2, y1: domain[1] * 2,
          line: { color: colors.asymptote, width: 2, dash: 'dash' }
        });
      });

      // Horizontal asymptote
      if (features.horizontalAsymptote !== null && features.horizontalAsymptote !== undefined) {
        shapes.push({
          type: 'line',
          x0: domain[0], x1: domain[1],
          y0: features.horizontalAsymptote, y1: features.horizontalAsymptote,
          line: { color: colors.asymptote, width: 2, dash: 'dash' }
        });
      }
    }

    // Axis of symmetry (for quadratic)
    if (showFeatures.includes('axis') && features.axisOfSymmetry !== undefined) {
      shapes.push({
        type: 'line',
        x0: features.axisOfSymmetry, x1: features.axisOfSymmetry,
        y0: domain[0] * 2, y1: domain[1] * 2,
        line: { color: colors.secondary, width: 1, dash: 'dot' }
      });
    }

    // Layout
    const yRange = this.calculateYRange(traces, domain);
    
    const layout = {
      xaxis: {
        range: domain,
        zeroline: true,
        zerolinewidth: 2,
        zerolinecolor: '#000',
        showgrid: gridLines,
        gridcolor: '#e5e7eb',
        dtick: 1
      },
      yaxis: {
        range: yRange,
        zeroline: true,
        zerolinewidth: 2,
        zerolinecolor: '#000',
        showgrid: gridLines,
        gridcolor: '#e5e7eb',
        dtick: 1,
        scaleanchor: pedagogicalMode ? 'x' : undefined,
        scaleratio: pedagogicalMode ? 1 : undefined
      },
      shapes,
      annotations,
      showlegend: false,
      margin: { l: 50, r: 20, t: 20, b: 50 },
      paper_bgcolor: 'rgba(0,0,0,0)',
      plot_bgcolor: 'rgba(0,0,0,0)'
    };

    return { traces, layout };
  }

  /**
   * Generate function trace data
   */
  static generateFunctionTrace(functionType, params, domain, step = 0.1) {
    const x = [];
    const y = [];
    const f = this.getFunction(functionType, params);

    for (let xi = domain[0]; xi <= domain[1]; xi += step) {
      const yi = f(xi);
      
      // Skip discontinuities
      if (isFinite(yi) && Math.abs(yi) < 1000) {
        x.push(roundTo(xi, 4));
        y.push(roundTo(yi, 4));
      } else {
        // Insert gap for discontinuity
        if (x.length > 0) {
          x.push(null);
          y.push(null);
        }
      }
    }

    return { x, y, mode: 'lines', type: 'scatter' };
  }

  /**
   * Get function evaluator
   */
  static getFunction(functionType, params) {
    switch (functionType) {
      case 'linear':
        return x => params.m * x + params.c;
      case 'quadratic':
        return x => params.a * Math.pow(x - params.p, 2) + params.q;
      case 'hyperbola':
        return x => {
          if (Math.abs(x + params.p) < 0.001) return NaN;
          return params.a / (x + params.p) + params.q;
        };
      case 'exponential':
        return x => params.a * Math.pow(params.b, x + params.p) + params.q;
      default:
        return () => 0;
    }
  }

  /**
   * Calculate appropriate Y range for display
   */
  static calculateYRange(traces, xRange) {
    let minY = -10, maxY = 10;
    
    for (const trace of traces) {
      if (!trace.y) continue;
      for (const y of trace.y) {
        if (y !== null && isFinite(y)) {
          minY = Math.min(minY, y);
          maxY = Math.max(maxY, y);
        }
      }
    }

    // Add padding
    const padding = (maxY - minY) * 0.1;
    return [
      Math.max(-50, minY - padding),
      Math.min(50, maxY + padding)
    ];
  }
}


// ═══════════════════════════════════════════════════════════════════════════════
// INSTANCE BUILDER (Main Orchestrator)
// ═══════════════════════════════════════════════════════════════════════════════

export class DynamicQuestionInstanceBuilder {
  constructor() {
    this.statementSelector = new StatementSelector();
  }

  /**
   * Build a complete question instance from a template
   */
  buildInstance(template, seed = null) {
    const effectiveSeed = seed || Date.now();
    const rng = new SeededRandom(effectiveSeed);
    const paramGenerator = new ParameterGenerator(effectiveSeed);

    // Attempt to build valid instance
    for (let attempt = 0; attempt < 10; attempt++) {
      const result = this.tryBuildInstance(template, rng, paramGenerator, attempt);
      if (result.success) {
        return {
          ...result.instance,
          seed: effectiveSeed,
          generatedAt: new Date().toISOString(),
          attempt: attempt + 1
        };
      }
    }

    // Return failure after max attempts
    return {
      success: false,
      error: 'Could not generate valid question instance after 10 attempts',
      seed: effectiveSeed
    };
  }

  /**
   * Try to build a single instance
   */
  tryBuildInstance(template, rng, paramGenerator, attemptNum) {
    // 1. Generate parameters
    const { params, warning } = paramGenerator.generateValid(
      template.parameterConfig,
      template.constraints
    );

    // 2. Build domain
    const domain = this.buildDomain(template.domainConfig, params, rng);

    // 3. Extract features
    const features = GraphFeatureExtractor.extractFeatures(
      template.functionType,
      params,
      domain
    );

    // Add computed helper values
    features.transformations = features.transformations || {};
    features.transformations.horizontalShiftAbs = Math.abs(params.p || 0);
    features.transformations.verticalShiftAbs = Math.abs(params.q || 0);

    // 4. Build evaluation context
    const context = {
      params,
      features,
      domain,
      rng
    };

    // 5. Select statements
    const selectionResult = this.getStatementSelector(template).selectStatements(
      template.functionType,
      context,
      template.statementConfig || {}
    );

    if (!selectionResult.success) {
      return { success: false, reason: selectionResult.reason };
    }

    // 6. Build graph config
    const graphConfig = GraphConfigBuilder.build(
      template.functionType,
      params,
      features,
      template.displayConfig
    );

    // 7. Assemble instance
    const instance = {
      success: true,
      templateId: template.id,
      functionType: template.functionType,
      params,
      domain: domain ? domain.toJSON() : null,
      features,
      equation: features.equation,
      options: selectionResult.options,
      graphConfig,
      validation: {
        satisfiable: true,
        correctCount: selectionResult.stats.correctCount,
        incorrectCount: selectionResult.stats.incorrectCount,
        categories: selectionResult.stats.categories
      },
      metadata: {
        subject: template.subject,
        grades: template.grades,
        topics: template.topics,
        marks: template.marks || selectionResult.options.filter(o => o.isCorrect).length
      }
    };

    return { success: true, instance };
  }

  /**
   * Build domain from configuration
   */
  buildDomain(domainConfig, params, rng) {
    if (!domainConfig) return null;

    const { type = 'full', min = -10, max = 10 } = domainConfig;

    switch (type) {
      case 'full':
        return null; // No restriction
      
      case 'fixed':
        return Interval.closed(min, max);
      
      case 'random':
        const width = rng.nextInt(domainConfig.minWidth || 5, domainConfig.maxWidth || 15);
        const center = params.p || 0;
        return Interval.closed(
          center - Math.floor(width / 2),
          center + Math.ceil(width / 2)
        );
      
      case 'vertex_centered':
        // For quadratics, center around vertex
        const p = params.p || 0;
        const halfWidth = rng.nextInt(3, 8);
        return Interval.closed(p - halfWidth, p + halfWidth);
      
      default:
        return null;
    }
  }

  /**
   * Validate an instance meets all requirements
   */
  validateInstance(instance) {
    const issues = [];

    // Check option counts
    const correctCount = instance.options.filter(o => o.isCorrect).length;
    const incorrectCount = instance.options.filter(o => !o.isCorrect).length;

    if (correctCount < 1) {
      issues.push('No correct options');
    }
    if (incorrectCount < 1) {
      issues.push('No incorrect options');
    }
    if (instance.options.length < 3) {
      issues.push('Too few options');
    }

    // Check for contradictions
    const contradictions = this.findContradictions(instance.options);
    if (contradictions.length > 0) {
      issues.push(`Contradictory options: ${contradictions.join(', ')}`);
    }

    return {
      valid: issues.length === 0,
      issues
    };
  }

  /**
   * Find contradictory correct options
   */
  findContradictions(options) {
    const contradictionPairs = [
      ['quad_opens_up', 'quad_opens_down'],
      ['quad_has_minimum', 'quad_has_maximum'],
      ['quad_range_gte_q', 'quad_range_lte_q'],
      ['lin_increasing', 'lin_decreasing'],
      ['exp_growth', 'exp_decay'],
      ['exp_always_increasing', 'exp_always_decreasing']
    ];

    const correctIds = options.filter(o => o.isCorrect).map(o => o.templateId);
    const found = [];

    for (const [a, b] of contradictionPairs) {
      if (correctIds.includes(a) && correctIds.includes(b)) {
        found.push(`${a} vs ${b}`);
      }
    }

    return found;
  }

  getStatementSelector(template) {
    const customStatements = normalizeCustomStatements(
      template.functionType,
      template.customStatements || []
    );
    const statementSource = template.statementSource || 'merged';

    if (statementSource === 'built_in') {
      return new StatementSelector(getTemplatesForFunction(template.functionType));
    }

    if (customStatements.length === 0) {
      return this.statementSelector;
    }

    if (statementSource === 'custom_only') {
      return new StatementSelector(customStatements);
    }

    const builtIn = getTemplatesForFunction(template.functionType);
    return new StatementSelector([...builtIn, ...customStatements]);
  }
}

function normalizeCustomStatements(functionType, customStatements) {
  return (customStatements || [])
    .map((statement, index) => ({
      id: statement.id || `custom_${index}`,
      category: statement.category || StatementCategory.STRUCTURAL,
      type: statement.type || StatementType.CONDITIONAL,
      text: statement.text || '',
      predicate: statement.predicate || { always: true },
      difficulty: typeof statement.difficulty === 'number' ? statement.difficulty : 2,
      functionTypes: Array.isArray(statement.functionTypes) && statement.functionTypes.length > 0
        ? statement.functionTypes
        : [functionType],
      targetSkill: statement.targetSkill || null,
      misconception: statement.misconception || null
    }))
    .filter(statement => statement.text);
}


// ═══════════════════════════════════════════════════════════════════════════════
// DEFAULT TEMPLATE CONFIGURATIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const DEFAULT_TEMPLATES = {
  quadratic_vertex_form: {
    id: 'quadratic_vertex_form',
    functionType: 'quadratic',
    parameterConfig: {
      a: { values: [-3, -2, -1, 1, 2, 3], exclude: [0] },
      p: { min: -5, max: 5, step: 1 },
      q: { min: -6, max: 6, step: 1 }
    },
    statementConfig: {
      includeCategories: [
        StatementCategory.STRUCTURAL,
        StatementCategory.ORIENTATION,
        StatementCategory.INTERCEPTS,
        StatementCategory.DOMAIN_RANGE,
        StatementCategory.INTERVALS
      ],
      totalOptions: { min: 5, max: 6 },
      correctOptions: { min: 2, max: 4 },
      incorrectOptions: { min: 2, max: 3 },
      requiredCategories: [StatementCategory.STRUCTURAL, StatementCategory.ORIENTATION],
      targetDifficulty: { min: 1, max: 3 }
    },
    displayConfig: {
      showGraph: true,
      showFeatures: ['vertex', 'intercepts', 'axis'],
      pedagogicalMode: true
    },
    subject: 'Mathematics',
    grades: [10, 11, 12],
    topics: ['Functions', 'Quadratic Functions'],
    marks: 4
  },

  linear_basic: {
    id: 'linear_basic',
    functionType: 'linear',
    parameterConfig: {
      m: { values: [-3, -2, -1, -0.5, 0.5, 1, 2, 3], exclude: [0] },
      c: { min: -5, max: 5, step: 1 }
    },
    statementConfig: {
      totalOptions: { min: 4, max: 5 },
      correctOptions: { min: 2, max: 3 },
      incorrectOptions: { min: 1, max: 2 },
      targetDifficulty: { min: 1, max: 2 }
    },
    displayConfig: {
      showGraph: true,
      showFeatures: ['intercepts']
    },
    subject: 'Mathematics',
    grades: [9, 10, 11],
    topics: ['Functions', 'Linear Functions'],
    marks: 3
  },

  hyperbola_standard: {
    id: 'hyperbola_standard',
    functionType: 'hyperbola',
    parameterConfig: {
      a: { values: [-4, -3, -2, -1, 1, 2, 3, 4], exclude: [0] },
      p: { min: -4, max: 4, step: 1 },
      q: { min: -4, max: 4, step: 1 }
    },
    statementConfig: {
      includeCategories: [
        StatementCategory.ASYMPTOTES,
        StatementCategory.INTERCEPTS,
        StatementCategory.DOMAIN_RANGE,
        StatementCategory.ORIENTATION,
        StatementCategory.INTERVALS
      ],
      totalOptions: { min: 5, max: 6 },
      correctOptions: { min: 2, max: 4 },
      incorrectOptions: { min: 2, max: 3 },
      requiredCategories: [StatementCategory.ASYMPTOTES],
      targetDifficulty: { min: 1, max: 3 }
    },
    displayConfig: {
      showGraph: true,
      showFeatures: ['intercepts', 'asymptotes']
    },
    subject: 'Mathematics',
    grades: [10, 11, 12],
    topics: ['Functions', 'Hyperbolic Functions'],
    marks: 4
  },

  exponential_standard: {
    id: 'exponential_standard',
    functionType: 'exponential',
    parameterConfig: {
      a: { values: [-2, -1, 1, 2], exclude: [0] },
      b: { values: [0.25, 0.5, 2, 3, 4] },
      p: { min: -3, max: 3, step: 1 },
      q: { min: -4, max: 4, step: 1 }
    },
    statementConfig: {
      includeCategories: [
        StatementCategory.ASYMPTOTES,
        StatementCategory.BEHAVIOUR,
        StatementCategory.INTERCEPTS,
        StatementCategory.DOMAIN_RANGE,
        StatementCategory.INTERVALS
      ],
      totalOptions: { min: 5, max: 6 },
      correctOptions: { min: 2, max: 4 },
      incorrectOptions: { min: 2, max: 3 },
      requiredCategories: [StatementCategory.ASYMPTOTES, StatementCategory.BEHAVIOUR],
      targetDifficulty: { min: 1, max: 3 }
    },
    displayConfig: {
      showGraph: true,
      showFeatures: ['intercepts', 'asymptotes']
    },
    subject: 'Mathematics',
    grades: [10, 11, 12],
    topics: ['Functions', 'Exponential Functions'],
    marks: 4
  }
};


// ═══════════════════════════════════════════════════════════════════════════════
// EXPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export const instanceBuilder = new DynamicQuestionInstanceBuilder();

export default {
  ParameterGenerator,
  StatementSelector,
  GraphConfigBuilder,
  DynamicQuestionInstanceBuilder,
  DEFAULT_TEMPLATES,
  instanceBuilder,
  
  // Convenience method
  buildInstance: (template, seed) => instanceBuilder.buildInstance(template, seed)
};
